import { useRoute } from 'vue-router';
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useStorage } from '@vueuse/core';
import useDashboard from '~/composables/useDashboard';

/**
 * Utilities to manage the layout of the application
 */
export default function useAppLayout() {
  const route = useRoute();
  const { currentDashboardId } = useDashboard();
  const rootRoute = computed(() => (route.matched.length > [0] ? route.matched[0] : undefined));
  // Name of the component that will be used as layout
  const layoutComponent = computed(() => {
    // If the root route is not defined return undefined, so we don't load any layout
    if (!rootRoute.value) return undefined;

    return ['DefaultLayout', 'FocusLayout', 'SettingsLayout'].indexOf(rootRoute.value?.meta?.layout) >= 0
      ? route?.meta?.layout
      : 'DefaultLayout';
  });

  const { mdAndUp, xlAndUp } = useDisplay();

  // The index of outside spacing to use along vuetify CSS classes
  const layoutOutsideSpacing = computed(() => {
    if (xlAndUp.value) return '8'; // 32px
    if (mdAndUp.value) return '6'; // 24px

    return '4'; // 16px
  });

  // Fullscreen mode stuff
  /**
   * Store the fact that the app is in fullscreen mode or not
   */
  const fullscreenRaw = useStorage('kp-layout-fullscreen', false);
  /**
   * Toggle the fullscreen mode
   */
  const fullscreenToggle = () => {
    fullscreenRaw.value = !fullscreenRaw.value;
  };
  /**
   * Icon to use for the fullscreen toggle button
   */
  const fullscreenIcon = computed(() => (fullscreenRaw.value ? 'close_fullscreen' : 'fullscreen'));
  /**
   * If the app is in fullscreen mode or not with special conditions
   */

  const fullscreenAllowed = computed(() => {
    if (currentDashboardId.value) return true;
    if (route.name === 'event' || route.name === 'eventId') return true;
    if (route.name === 'categorization') return true;
    if (route.name === 'machineDetail') return true;
    return false;
  });

  const fullscreen = computed(() => fullscreenRaw.value && fullscreenAllowed.value);

  return {
    fullscreen,
    fullscreenIcon,
    fullscreenToggle,
    layoutComponent,
    layoutOutsideSpacing,
  };
}
